import { useState } from "react";
import { useForm } from "react-hook-form";

import { RunGroup } from "./runGroups";
import {
	Disposition,
	MechanicalResultValues,
	MechanicalRunEdit,
} from "./types";

type SpecimenField = keyof MechanicalResultValues;

type RunForm = {
	specimens: Record<SpecimenField, string>[];
	comment: string;
	disposition: Disposition;
};

const EMPTY_FORM: RunForm = { specimens: [], comment: "", disposition: "U" };

const toInput = (value: number | null) => (value == null ? "" : String(value));
const toValue = (value: string) => (value.trim() === "" ? null : Number(value));

const toDefaults = (group: RunGroup): RunForm => ({
	specimens: group.results.map((result) => ({
		yieldValue: toInput(result.YieldValue),
		tensileValue: toInput(result.TensileValue),
		elongationValue: toInput(result.ElongationValue),
		percentRAValue: toInput(result.PercentRAValue),
	})),
	comment: group.comment ?? "",
	disposition: group.disposition?.Disposition ?? "U",
});

type Params = {
	onSaveRun?: (
		mechanicalTestRunId: number,
		edit: MechanicalRunEdit,
	) => Promise<unknown> | void;
};

export const useRunEditor = ({ onSaveRun }: Params) => {
	const [editingRunId, setEditingRunId] = useState<number | null>(null);

	const form = useForm<RunForm>({
		defaultValues: EMPTY_FORM,
		mode: "onChange",
	});
	const { dirtyFields, errors, isValid } = form.formState;

	const start = (group: RunGroup) => {
		form.reset(toDefaults(group));
		setEditingRunId(group.runId);
	};

	const cancel = () => {
		setEditingRunId(null);
		form.reset(EMPTY_FORM);
	};

	const save = (group: RunGroup) =>
		form.handleSubmit(async (values) => {
			if (!onSaveRun) return;

			const results = group.results.flatMap((result, index) => {
				const specimen = values.specimens[index];
				const specimenDirty = dirtyFields.specimens?.[index];
				if (!specimen || !specimenDirty) return [];
				if (!Object.values(specimenDirty).some(Boolean)) return [];

				return [
					{
						result,
						values: {
							yieldValue: toValue(specimen.yieldValue),
							tensileValue: toValue(specimen.tensileValue),
							elongationValue: toValue(specimen.elongationValue),
							percentRAValue: toValue(specimen.percentRAValue),
						},
					},
				];
			});

			try {
				await onSaveRun(group.runId, {
					results,
					disposition: {
						value: values.disposition,
						comment: values.comment.trim() || null,
						changed: !!dirtyFields.comment || !!dirtyFields.disposition,
						dispositionId: dirtyFields.disposition
							? null
							: (group.disposition?.MechanicalTestRunDispositionId ?? null),
					},
				});
				cancel();
			} catch {}
		})();

	return {
		isEditing: (runId: number) => editingRunId === runId,
		isIdle: editingRunId === null,
		hasInvalid: !isValid,
		control: form.control,
		commentField: form.register("comment"),
		valueField: (index: number, field: SpecimenField) => ({
			registration: form.register(`specimens.${index}.${field}` as const, {
				validate: (value: string) =>
					value.trim() === "" || !Number.isNaN(Number(value)) || "Not a number",
			}),
			error: !!errors.specimens?.[index]?.[field],
		}),
		start,
		cancel,
		save,
	};
};
